import { useState } from 'react';
import { useCostSummary } from '../hooks/useApi';

/**
 * Badge global de tracking dual (task E2) — header compacto + popover con detalle.
 *
 * Las 3 columnas obligatorias por decisión del usuario 2026-04-25 ("toda acción de IA carga
 * su monto, sin excepción"):
 *  - 🟡 fal.ai (USD pay-per-use)
 *  - 🟢 ElevenLabs cuenta propia (% del plan que **xVision** consumió este período)
 *  - 🔵 Anthropic LLM (USD pay-per-use)
 *
 * Decisión 2026-04-29: el % de ElevenLabs muestra solo lo que esta app gastó del plan,
 * NO el % global de la cuenta (que incluye consumo de otros sistemas/personas que comparten
 * la API key). Para ver el consumo total de la cuenta, usar /admin/elevenlabs-account.
 *
 * Color del % de ElevenLabs según escalón (Voice Cost Advisor task #61):
 *  - 0-79%: verde   (info passive)
 *  - 80-99%: amarillo (aviso)
 *  - 100-149%: naranja (overage)
 *  - ≥150%: rojo (sugerir Pool B / upgrade)
 */
export function CostBadge() {
  const { data, isLoading } = useCostSummary();
  const [open, setOpen] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="text-xs text-slate-500 px-2 py-1 border border-slate-700 rounded">
        cargando…
      </div>
    );
  }

  const elevenLive = data.elevenlabs.live;
  const charsTracked = data.elevenlabs.charsTracked;
  // % de xVision sobre el plan vigente. Si no se pudo consultar el plan, fallback a 0.
  const elevenPercent = elevenLive && elevenLive.characterLimit > 0
    ? (charsTracked / elevenLive.characterLimit) * 100
    : 0;
  const elevenColor = colorForPercent(elevenPercent);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1 text-xs border border-slate-700 rounded hover:border-slate-500 bg-slate-900/60"
        title="Detalle de gastos · click para abrir"
      >
        <span className="text-slate-400">
          🟡 <span className="text-slate-200">${data.fal.totalUsd.toFixed(2)}</span>
        </span>
        <span className={elevenColor.text}>
          🟢 <span className="font-medium">{elevenPercent.toFixed(0)}%</span>
        </span>
        <span className="text-slate-400">
          🔵 <span className="text-slate-200">${data.llm.totalUsd.toFixed(2)}</span>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl p-4 z-50 text-xs">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-2 right-2 text-slate-500 hover:text-slate-300"
          >
            ✕
          </button>
          <h3 className="text-sm font-semibold text-slate-100 mb-3">Costos acumulados</h3>

          {/* fal.ai */}
          <div className="mb-3 pb-3 border-b border-slate-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-300">🟡 fal.ai (pay-per-use)</span>
              <span className="font-mono text-slate-100">${data.fal.totalUsd.toFixed(4)}</span>
            </div>
          </div>

          {/* ElevenLabs — solo lo que xVision consumió */}
          <div className="mb-3 pb-3 border-b border-slate-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-300">🟢 ElevenLabs (xVision)</span>
              <span className={`font-mono font-semibold ${elevenColor.text}`}>
                {elevenPercent.toFixed(1)}%
              </span>
            </div>
            {elevenLive ? (
              <div className="space-y-0.5 text-[11px] text-slate-400">
                <div>
                  xVision usó: <span className="text-slate-200">{charsTracked.toLocaleString()}</span>
                  {' / '}
                  <span className="text-slate-200">{elevenLive.characterLimit.toLocaleString()}</span> chars
                  del plan
                </div>
                <div>
                  Plan: <span className="text-slate-200">{elevenLive.tier}</span>
                  {' · reset '}
                  <span className="text-slate-200">
                    {elevenLive.nextResetIso ? new Date(elevenLive.nextResetIso).toLocaleString() : 'N/D'}
                  </span>
                </div>
                <div className="pt-1">
                  <ProgressBar percent={elevenPercent} colorClass={elevenColor.bar} />
                </div>
                {elevenPercent >= 100 && (
                  <div className="mt-2 px-2 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 text-[11px]">
                    ⚠️ xVision superó el plan — Pool B (fal.ai) o upgrade.
                  </div>
                )}
                {elevenPercent >= 80 && elevenPercent < 100 && (
                  <div className="mt-2 px-2 py-1 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-500/30 text-[11px]">
                    xVision se acerca al límite. Quedan{' '}
                    {(elevenLive.characterLimit - charsTracked).toLocaleString()} chars.
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-slate-800 text-[10px] text-slate-500">
                  El consumo total del plan (xVision + otros sistemas) lo ves en{' '}
                  <span className="text-vision">/admin/elevenlabs-account</span>.
                </div>
              </div>
            ) : (
              <div className="text-[11px] text-slate-500 italic">
                {data.elevenlabs.liveError
                  ? `No se pudo consultar plan: ${data.elevenlabs.liveError}`
                  : 'Sin datos del plan ElevenLabs todavía.'}
                <div className="mt-1">
                  xVision trackeó: {charsTracked.toLocaleString()} chars
                </div>
              </div>
            )}
          </div>

          {/* Anthropic */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-300">🔵 Anthropic LLM (pay-per-use)</span>
              <span className="font-mono text-slate-100">${data.llm.totalUsd.toFixed(4)}</span>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-slate-800 text-[10px] text-slate-500">
            * ElevenLabs no suma al total USD porque es plan mensual fijo (costo marginal ≈ 0).
            Total pay-per-use: ${data.totalUsd.toFixed(4)}.
          </div>
        </div>
      )}
    </div>
  );
}

function colorForPercent(p: number): { text: string; bar: string } {
  if (p >= 150) return { text: 'text-red-400', bar: 'bg-red-500' };
  if (p >= 100) return { text: 'text-orange-400', bar: 'bg-orange-500' };
  if (p >= 80) return { text: 'text-yellow-400', bar: 'bg-yellow-500' };
  return { text: 'text-emerald-400', bar: 'bg-emerald-500' };
}

function ProgressBar({ percent, colorClass }: { percent: number; colorClass: string }) {
  // Visualmente capamos el bar a 100% pero mostramos el % real al lado.
  const capped = Math.min(percent, 100);
  return (
    <div className="w-full h-1.5 bg-slate-800 rounded overflow-hidden">
      <div className={`h-full ${colorClass}`} style={{ width: `${capped}%` }} />
    </div>
  );
}
